import { NavigationMarkerObserver } from "./navigation-markers";
import { initialNavigation } from "./navigation-load";
import { NavigationVisit } from "./navigation-visit";
import {
  committedTransition,
  createVisit,
  startTransition,
} from "./navigation-transition";
import type { NavigationStart } from "./navigation-readiness-policy";

let visit: NavigationVisit | null = null;
let committed: { readonly path: string; readonly search: string };
let observer: NavigationMarkerObserver;
let release: string;

function finish(): void {
  observer.stop();

  if (!visit) return;

  visit.finish();
  visit = null;
  observer.clear();
}

export function commitNavigation({
  path,
  search = new URLSearchParams(location.search).toString(),
}: {
  readonly path: string;
  readonly search?: string | undefined;
}): void {
  if (path !== location.pathname) return;

  committed = { path, search };

  if (!visit) return;

  const transition = committedTransition({
    current: visit.record,
    path,
    search,
  });

  if (transition.kind === "finish") finish();
  else if (transition.kind === "redirect") {
    visit.redirect(transition.target);
  }
}

function inspectMarkers(): void {
  commitNavigation({ path: location.pathname });
  const current = visit;

  if (!current) return;

  observer.flush((entry) => current.settle(entry));

  // Content HTML can be ready before hydration; shell and personal controls register from effects.
  if (current.record.settled.has("content")) observer.stop();
}

export function registerNavigationMarker(marker: HTMLElement): () => void {
  const unregister = observer.register(marker);

  if (visit) observer.schedule();

  return unregister;
}

export function startNavigation({
  url,
  navigation,
  started = performance.now(),
  load = "soft",
  redirected = false,
}: NavigationStart): void {
  const transition = startTransition({
    current: visit?.record ?? null,
    committed,
    url,
    navigation,
    load,
  });

  if (transition.kind === "unchanged") return;

  if (transition.kind === "redirect" && visit) {
    visit.redirect(transition.target);
    observer.schedule();
    return;
  }

  finish();

  if (transition.kind !== "start") return;

  const record = createVisit({
    target: transition.target,
    committed,
    navigation,
    started,
    load,
    redirected,
  });
  visit = new NavigationVisit({
    record,
    release,
    onDeadline: () => commitNavigation({ path: location.pathname }),
  });
  visit.start();
  observer.start();
  observer.schedule();
}

export function installNavigationReadiness(config: {
  readonly release: string;
}): void {
  release = config.release;
  // History traversal changes location before the router reports its start.
  committed = {
    path: location.pathname,
    search: new URLSearchParams(location.search).toString(),
  };
  observer = new NavigationMarkerObserver({ onInspect: inspectMarkers });
  startNavigation({
    url: location.href,
    ...initialNavigation(),
    started: 0,
    load: "cold",
  });
  document.addEventListener("visibilitychange", () => {
    if (visit && document.visibilityState !== "visible")
      visit.record.foreground = false;
  });
  window.addEventListener("pagehide", finish);
  window.addEventListener("pageshow", (event) => {
    if (event.persisted)
      startNavigation({
        url: location.href,
        navigation: "restore",
        started: performance.now(),
        load: "restore",
      });
  });
}
